import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { Save } from 'lucide-react';

interface TenantSettings {
  id: string;
  name: string;
  slug: string;
  contact_email: string;
  primary_color: string;
  subscription_tier: string;
}

const PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
];

export function FacilitySettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [color, setColor] = useState('#c87941');
  const [province, setProvince] = useState('BC');

  useEffect(() => { loadSettings(); }, [user]);

  const loadSettings = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      const { data: tenant } = await supabase.from('tenants').select('*').eq('id', profile.tenant_id).single();
      if (tenant) {
        setSettings(tenant);
        setName(tenant.name || '');
        setEmail(tenant.contact_email || '');
        setColor(tenant.primary_color || '#c87941');
        setProvince(tenant.slug || 'BC');
      }
    } catch (e) { setError('Failed to load settings'); }
    setLoading(false);
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    try {
      await supabase.from('tenants').update({
        name,
        contact_email: email,
        primary_color: color,
        slug: province,
      }).eq('id', settings.id);
      setDirty(false);
    } catch (e) { setError('Failed to save'); }
    setSaving(false);
  };

  const change = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setter(e.target.value);
    setDirty(true);
  };

  if (loading) return <main className="flex-1 p-6"><p className="text-[rgb(var(--admin-text-muted))]">Loading settings...</p></main>;
  if (!settings) return <main className="flex-1 p-6"><p className="text-[rgb(var(--admin-text-muted))]">No facility found</p></main>;

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--admin-text-primary))]">Facility Settings</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">
            Manage your facility profile and system configuration
            {dirty && <span className="ml-2 text-xs text-[rgb(var(--admin-warning))]">— Unsaved changes</span>}
          </p>
        </div>
        <button onClick={saveSettings} disabled={saving} className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white text-sm font-medium hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && <p className="text-sm text-[rgb(var(--admin-danger))]">{error}</p>}

      {/* 1. Facility Information */}
      <div className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-4">
        <h2 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">Facility Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Facility Name</label>
            <input type="text" value={name} onChange={change(setName)} placeholder="Enter facility name" className="w-full px-4 py-3 rounded-2xl bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-[rgb(var(--admin-text-primary))] focus:border-[rgb(var(--admin-accent))] focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Contact Email</label>
            <input type="email" value={email} onChange={change(setEmail)} placeholder="Enter contact email" className="w-full px-4 py-3 rounded-2xl bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-[rgb(var(--admin-text-primary))] focus:border-[rgb(var(--admin-accent))] focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Province</label>
            <select value={province} onChange={change(setProvince)} className="w-full px-4 py-3 rounded-2xl bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-[rgb(var(--admin-text-primary))] focus:border-[rgb(var(--admin-accent))] focus:outline-none transition-colors">
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* 2. Branding */}
      <div className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-4">
        <h2 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">Branding</h2>
        <div>
          <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-2">Primary Color</label>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl border border-[rgb(var(--admin-border))]" style={{ backgroundColor: color }} />
            <input type="text" value={color} onChange={change(setColor)} className="px-3 py-2 rounded-xl bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-sm text-[rgb(var(--admin-text-primary))] font-mono focus:border-[rgb(var(--admin-accent))] focus:outline-none transition-colors" />
            <input type="color" value={color} onChange={change(setColor)} className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent" />
          </div>
          <p className="text-xs text-[rgb(var(--admin-text-muted))] mt-2">Used for buttons and highlights across the system</p>
        </div>
      </div>

      {/* 3. Plan */}
      <div className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
        <div className="flex items-center justify-between">
          <span className="text-base font-medium text-[rgb(var(--admin-text-primary))]">
            {settings.subscription_tier === 'free' ? 'Free Trial' : settings.subscription_tier || 'Free Trial'}
          </span>
          <span className="text-sm text-[rgb(var(--admin-text-muted))]">
            {settings.subscription_tier || 'trial'}
          </span>
        </div>
        <p className="text-xs text-[rgb(var(--admin-text-muted))] mt-1">Upgrade to unlock full features</p>
      </div>
    </main>
  );
}